import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@consigli/shadcn';
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { useState, useMemo } from 'react';

import { calculateInputWidth } from '../shared';

type Option = {
  value: string;
  label: string;
};

interface ComboboxProps {
  optionGroups: {
    label: string;
    options: Option[];
  }[];
  fallbackLabel: string;
  placeholder: string;
  value: string[];
  onValueChange: (value: string[]) => void;
  className?: string;
  search?: boolean;
  searchPlaceholder?: string;
  fullWidth?: boolean; // New prop to control width behavior
}

export function Combobox({
  optionGroups,
  fallbackLabel,
  placeholder,
  value,
  onValueChange,
  className,
  search = true,
  searchPlaceholder = 'Search...',
  fullWidth = false,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);

  // Calculate dynamic width based on placeholder text (only when not fullWidth and no className)
  const dynamicWidth = useMemo(() => {
    if (fullWidth) return 'w-full';
    if (className) return ''; // Don't apply dynamic width if className is provided
    return calculateInputWidth(placeholder, false);
  }, [placeholder, fullWidth, className]);

  const triggerLabel = useMemo(() => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const match = optionGroups
        .flatMap((group) => group.options)
        .find((option) => option.value === value[0]);
      return match?.label ?? placeholder;
    }
    return `${value.length} selected`;
  }, [value, optionGroups, placeholder]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('h-[40px] min-w-0 justify-between', className, dynamicWidth)}
        >
          <span className="min-w-0 flex-1 truncate text-left" title={triggerLabel}>
            {triggerLabel}
          </span>
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0">
        <Command>
          {search && <CommandInput placeholder={searchPlaceholder} />}
          <CommandList>
            <CommandEmpty>{fallbackLabel}</CommandEmpty>
            {optionGroups.map((group) => (
              <CommandGroup key={group.label} heading={group.label}>
                {group.options.map((option) => (
                  <CommandItem
                    className="cursor-pointer"
                    key={option.value}
                    value={option.value}
                    keywords={[option.label]}
                    onSelect={(currentValue) => {
                      const isSelected = value.includes(currentValue);
                      const newValue = isSelected
                        ? value.filter((v) => v !== currentValue)
                        : [...value, currentValue];
                      onValueChange(newValue);
                      // Don't close the popover for multi-select
                    }}
                  >
                    <CheckIcon
                      className={cn(
                        'mr-2 h-4 w-4',
                        value.includes(option.value) ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
